import { getTotalCommitment } from '@weft/services';
import { filterFixedRevisionedLineItems } from '@weft/services';
import { DEFAULT_CURRENCY } from '@weft/types';
import { isNotEmptyArray } from '@weft/utils';
import { useMemo } from 'react';
import React from 'react';
import { Helmet } from 'react-helmet';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useInvoice } from '../../../hooks/requests/use-invoice';
import { useLineItemsSplitter } from '../../../hooks/use-line-items-splitter';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import { getCurrencySymbol } from '../../../services/currency-symbol-mapper';
import { InvoiceFixedFeeCharges } from './invoice-components/InvoiceFixedFeeCharges';
import { InvoiceHeader } from './invoice-components/InvoiceHeader';
import { InvoicePromotions } from './invoice-components/InvoicePromotions';
import { InvoiceSidebar } from './invoice-components/InvoiceSidebar';
import { InvoiceSummary } from './invoice-components/InvoiceSummary';
import { InvoiceUsageBasedCharges } from './invoice-components/InvoiceUsageBasedCharges/InvoiceUsageBasedCharges';

const InvoiceViewPage: React.FC = () => {
  const { invoiceId } = useValidatedParams<['invoiceId']>('invoiceId');
  const { data } = useInvoice(invoiceId);
  const {
    fixedLineItems,
    usageLineItems,
    tiersUsageLineItems,
    perUnitUsageLineItems,
    commitmentLineItems,
    promotionLineItems,
  } = useLineItemsSplitter(data.invoice.lineItems);
  const totalCommitment = useMemo(() => getTotalCommitment(commitmentLineItems), [commitmentLineItems]);
  const hasUsageBasedCharges = isNotEmptyArray(usageLineItems) || isNotEmptyArray(commitmentLineItems);

  const { revisionedLineItems = [] } = data.invoice;
  const fixedRevisionedLineItems = filterFixedRevisionedLineItems(revisionedLineItems);

  const { contract, ...invoice } = data.invoice;
  const { revenueBreakdown } = invoice;
  const currencySymbol = getCurrencySymbol(contract?.customer.settings?.currency);

  const currency = contract?.customer.settings?.currency ?? DEFAULT_CURRENCY; 

  return (
    <div className='bg-selected flex h-auto'>
      <Helmet>
        <title>{`Invoice ${data.invoice.name}`}</title>
      </Helmet>
      <InvoiceSidebar {...{ invoice, totalPrice: revenueBreakdown.total, currencySymbol }} />
      <div className="bg-selected min-h-screen w-full">
        <div className='h-full w-full p-5'>
          <div className='rounded-lg bg-white px-9 py-12'>
            <InvoiceHeader 
              commitment={totalCommitment}
              contract={contract!}
              totalPrice={revenueBreakdown.total}
              invoice={invoice}
              currencySymbol={currencySymbol}
            />
            {isNotEmptyArray(fixedLineItems) && <InvoiceFixedFeeCharges {...{ fixedLineItems, revisionedLinedItems: fixedRevisionedLineItems }}/>}
            {hasUsageBasedCharges && <InvoiceUsageBasedCharges {...{ usageLineItems, tiersUsageLineItems, perUnitUsageLineItems, commitmentLineItems }} />}
            {isNotEmptyArray(promotionLineItems) && <InvoicePromotions {...{ promotionLineItems, currency }} />}
            <div className="mt-8">
              <InvoiceSummary
                revenueBreakdown={revenueBreakdown}
                currencySymbol={currencySymbol}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InvoiceViewPageWithSuspenseBoundary = withSuspenseBoundary(InvoiceViewPage);

export { InvoiceViewPageWithSuspenseBoundary as InvoiceViewPage };
